import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { ControlsHubPanelsAssociatedToRisk } from '@interfaces/central-monitoring';
import { ExtendedCompleteRiskFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import {
    assignCmControlChartsToRisk,
    assignCmControlChartsToRiskVariables,
} from '@queries/generated/risk-assessment.generated';
import { ASSIGN_CM_CONTROLS, GET_COMPLETE_RISKS_RISK_LOG } from '@queries/risk-assessment';

interface UnassignControlProps {
    onClose: () => void;
    onSubmit: (result: string) => void;
    risk: ExtendedCompleteRiskFields;
    control: ControlsHubPanelsAssociatedToRisk;
}

const UnassignCmControlDialog = ({ onClose, onSubmit, risk, control }: UnassignControlProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.unassignCmControl' });
    const { current } = use(RiskLogContext);

    const [assignControlsMutation] = useMutation<assignCmControlChartsToRisk, assignCmControlChartsToRiskVariables>(
        ASSIGN_CM_CONTROLS,
    );

    const handleSubmit = async () => {
        assignControlsMutation({
            variables: {
                id: risk.id,
                cmControlChartsIds: (risk.associatedControlsHubPanelIds ?? []).filter((id) => id !== control.panelRef),
            },
            refetchQueries: [
                {
                    query: GET_COMPLETE_RISKS_RISK_LOG,
                    variables: { studyId: current?.study?.id },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.assignCmControlCharts) {
                    onSubmit('error');
                    return;
                }
                onSubmit('success');
            })
            .catch(() => {
                onSubmit('error');
            });
    };

    return (
        <ConfirmationDialog
            title={t('title')}
            dataTestId="unassign-cm-control-confirmation-dialog"
            onClose={() => onClose()}
            onSubmit={() => handleSubmit()}
        >
            <div>
                <InfoField
                    type="Info"
                    message={
                        <div style={{ width: '438px', margin: '0 0' }}>
                            <div className="k-font-medium">{t('riskStatement')}</div>
                            <div>{risk?.statement?.slice(0, 600)}</div>
                            <br />
                            <div className="k-d-flex k-align-items-center">
                                <div className="k-font-medium">{t('controlDescription')}</div>
                            </div>
                            <div>{control?.name ?? ''}</div>
                        </div>
                    }
                />
                <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '18px' }}>{t('message')}</div>
            </div>
        </ConfirmationDialog>
    );
};

export default UnassignCmControlDialog;
